// Importing the required types for the API responses from UserProfileDetails module
import {
  IBaseResponse,
  IChangePasswordPayload,
  IInitiateExitPayload,
  IStatusRepsonse,
  IUserProfileDetailsServiceResponse,
} from "../../../Types/UserProfileDetails";
import ApiService from "../../ApiService";

// Using an enum to centralize all the API endpoints related to user profile details
enum EmployeeEndpoints {
  PROFILE_DETAILS = "/employee/profile/",
  OFFICIAL_DETAILS = "/employee/official-details",
  EDUCATIONAL_DETAILS = "/employee/educational-details",
  PERSONAL_DETAILS = "/employee/personal-details",
  FAMILY_DETAILS = "/employee/family-details",
  WORK_DETAILS = "/employee/work-details",
  FINANCIAL_DETAILS = "/employee/finance-details",
  UPDATE_PROFILE_PIC = "/employee/user/update-profile-image",
  SEND_RESET_EMAIL = "/send-email",
  CHANGE_PASSWORD = "/reset-password/change-password",
  INITIATE_EXIT = "/initiate-exit",
  EMPLOYEE_TYPES = "/user-types",
  EMPLOYEE_BANK = "/banks",
  EMPLOYEE_JOB_TYPES = "/job-types",
}

export interface IEmployeesRolesServiceResponse {
  data: Array<string>;
  message: string;
}

export interface IEmployeeBanksList {
  id: number;
  name: string;
}

export interface IEmployeeBanksListServiceResponse extends IStatusRepsonse {
  data: Array<IEmployeeBanksList>;
}

export interface IEmployeeJobTypes {
  id: number;
  name: string;
  type: string;
  description: string;
}

export interface IEmployeeJobTypesService extends IStatusRepsonse {
  data: Array<IEmployeeJobTypes>;
}
// Define the EmployeeDetailsService class which extends ApiService
// This service handles API interactions related to user profile details
class EmployeeDetailsService extends ApiService {
  // Private method to handle submitting details to various endpoints (reusable logic)
  private async submitDetails(
    endpoint: string,
    data: FormData
  ): Promise<IBaseResponse> {
    return this.post<IBaseResponse>(endpoint, data);
  }

  // Method to get user profile details by user ID
  getProfileDetailsService(
    id: string
  ): Promise<IUserProfileDetailsServiceResponse> {
    return this.get<IUserProfileDetailsServiceResponse>(
      `${EmployeeEndpoints.PROFILE_DETAILS}${id}`
    );
  }

  // Method to create or update official information for the user
  createUpdateOfficialInformation(
    employeeOfficialInformationData: FormData
  ): Promise<IBaseResponse> {
    return this.submitDetails(
      EmployeeEndpoints.OFFICIAL_DETAILS,
      employeeOfficialInformationData
    );
  }

  // Method to create or update educational information for the user
  createUpdateEducationInformation(
    employeeEducationInformation: FormData
  ): Promise<IBaseResponse> {
    return this.submitDetails(
      EmployeeEndpoints.EDUCATIONAL_DETAILS,
      employeeEducationInformation
    );
  }

  // Method to create or update personal information for the user
  createUpdatePersonalInformation(
    employeePersonalInformation: FormData
  ): Promise<IBaseResponse> {
    return this.submitDetails(
      EmployeeEndpoints.PERSONAL_DETAILS,
      employeePersonalInformation
    );
  }

  // Method to create or update family information for the user
  createUpdateFamilyInformation(
    employeeFamilyInformation: FormData
  ): Promise<IBaseResponse> {
    return this.submitDetails(
      EmployeeEndpoints.FAMILY_DETAILS,
      employeeFamilyInformation
    );
  }

  // Method to create or update work information for the user
  createUpdateWorkDetails(workInformation: FormData): Promise<IBaseResponse> {
    return this.submitDetails(EmployeeEndpoints.WORK_DETAILS, workInformation);
  }

  createUpdateFinancialDetails(
    employeeFinancialInformation: FormData,
    financialUserId: number
  ): Promise<IBaseResponse> {
    return this.submitDetails(
      `${EmployeeEndpoints.FINANCIAL_DETAILS}/${financialUserId}`,
      employeeFinancialInformation
    );
  }

  updateProfilePic(params: FormData): Promise<IBaseResponse> {
    return this.submitDetails(
      `${EmployeeEndpoints.UPDATE_PROFILE_PIC}`,
      params
    );
  }

  async sendResetPasswordEmail(email: string): Promise<IBaseResponse> {
    return this.post<IBaseResponse>(EmployeeEndpoints.SEND_RESET_EMAIL, {
      email,
    });
  }

  async changePassword(
    id: number | string,
    passwordPayload: IChangePasswordPayload
  ): Promise<IBaseResponse> {
    return this.post<IBaseResponse>(
      `${EmployeeEndpoints.CHANGE_PASSWORD}/${id}`,
      passwordPayload
    );
  }

  async initiateExit(payload: IInitiateExitPayload): Promise<IBaseResponse> {
    return this.post<IBaseResponse>(EmployeeEndpoints.INITIATE_EXIT, payload);
  }

  async getEmployeeTypes(): Promise<IEmployeesRolesServiceResponse> {
    return this.get<IEmployeesRolesServiceResponse>(
      EmployeeEndpoints.EMPLOYEE_TYPES
    );
  }
  async getEmployeeBanksList(): Promise<IEmployeeBanksListServiceResponse> {
    return this.get<IEmployeeBanksListServiceResponse>(
      EmployeeEndpoints.EMPLOYEE_BANK
    );
  }
  async getEmployeeJobTypesList(): Promise<IEmployeeJobTypesService> {
    return this.get<IEmployeeJobTypesService>(
      EmployeeEndpoints.EMPLOYEE_JOB_TYPES
    );
  }
}

// Create an instance of EmployeeDetailsService and export it for use in other parts of the application
export const employeeDetailService = new EmployeeDetailsService();
